import { differenceInYears, format, isToday, isYesterday } from 'date-fns';
import { NotificationType } from '@/api/apis/communication';

enum DateGroup {
  Today = 'Today',
  Yesterday = 'Yesterday',
}

export const getDateGroupKey = (date: string | Date): string => {
  const parsedDate = typeof date === 'string' ? new Date(date) : date;

  if (isToday(parsedDate)) {
    return DateGroup.Today;
  }

  if (isYesterday(parsedDate)) {
    return DateGroup.Yesterday;
  }

  const now = new Date();
  const yearsDifference = differenceInYears(now, parsedDate);

  // If the notification is from more than a year ago, include the year in the format
  if (yearsDifference >= 1) {
    return format(parsedDate, 'MMMM d, yyyy');
  }

  return format(parsedDate, 'MMMM d');
};

export const groupNotifications = (notifications: NotificationType[]): { [key: string]: NotificationType[] } => {
  return notifications.reduce(
    (acc, notification) => {
      const dateKey = getDateGroupKey(notification.createdAt);

      if (!acc[dateKey]) {
        acc[dateKey] = [];
      }

      acc[dateKey].push(notification);

      return acc;
    },
    {} as { [key: string]: NotificationType[] },
  );
};

export const sortKeys = (groupedNotifications: { [key: string]: NotificationType[] }): string[] => {
  const keys = Object.keys(groupedNotifications);
  const priorityOrder = [DateGroup.Today as string, DateGroup.Yesterday as string];

  return keys.sort((a, b) => {
    const aIndex = priorityOrder.indexOf(a);
    const bIndex = priorityOrder.indexOf(b);

    // If both are priority keys, sort by priority order
    if (aIndex !== -1 && bIndex !== -1) {
      return aIndex - bIndex;
    }

    // If only a is a priority key, it comes first
    if (aIndex !== -1) {
      return -1;
    }

    // If only b is a priority key, it comes first
    if (bIndex !== -1) {
      return 1;
    }

    // For date keys, sort by most recent first
    // Get the first notification from each group to compare dates
    const aDate = groupedNotifications[a]?.[0]?.createdAt;
    const bDate = groupedNotifications[b]?.[0]?.createdAt;

    if (aDate && bDate) {
      return new Date(bDate).getTime() - new Date(aDate).getTime();
    }

    return 0;
  });
};
